import { useEffect, useState } from "react";
import ReactHlsPlayer from "react-hls-player";
import { BiLoader } from "react-icons/bi";
import { FaRegStar, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import intApi from "../../api/int_api";
import orgApi from "../../api/org_api";
import ScoringDefinition from "../../component/FeedbackComponent/ScoringDefinition";
import { GS, Timer } from "../../Services";
import { Dropdown, ProfileImageUpload, TextArea } from "./AddFeedbackTech";
import FeedbackSubmitted from "./components/FeedbackSubmitted";

interface InterviewFeedbackProps {
  candidate_no_show: boolean;
  proxy: boolean;
  mode: string;
  level: string;
  communication_skill: string;
  junior: string;
  mid: string;
  senior: string;
  lead: string;
  architect: string;
  observation: string;
  attitude_confidence_cmd: string;
  problem_solving_cmd: string;
  inter_personal_skill_cmd: string;
  professionalism_cmd: string;
  recommended_status: string;
  comments: string;
  eligible_for_this_position: boolean;
  url: string | null;
}

const INITIAl_FEEDBACK_DATA: InterviewFeedbackProps = {
  candidate_no_show: false,
  proxy: false,
  mode: "",
  level: "",
  communication_skill: "1",
  junior: "",
  mid: "",
  senior: "",
  lead: "",
  architect: "",
  observation: "",
  attitude_confidence_cmd: "",
  inter_personal_skill_cmd: "",
  professionalism_cmd: "",
  problem_solving_cmd: "",
  recommended_status: "",
  comments: "",
  eligible_for_this_position: false,
  url: null,
};

interface CandidateProps {
  id: number;
  first_name: string;
  last_name: string;
}

interface InterviewerProps {
  id: number;
  first_name: string;
  last_name: string;
  candidate: CandidateProps;
}

interface InterviewScheduleDetailProps {
  feedback_status: string;
  feedback: { id: string }[];
  org: { first_name: string; last_name: string };
  interviewer: InterviewerProps;
  candidate: CandidateProps;
  utc_starttime: string;
  status: string;
  video_url: string | null;
}

interface SkillInputProps {
  skill_name: string;
  skill_id: string;
  weightage?: number;
  skill_rating: number;
  experience: string;
  error?: string;
}

const FINAL_RECOMMENDATION_FIELDS = [
  {
    label: "Must Hire / Hire / Hold / Do not Hire",
    name: "recommended_status",
  },
  {
    label: "Junior / Mid / Senior / Lead / Architect",
    name: "professionalism_cmd",
  },
  { label: "Other Comments, if any:", name: "comments" },
];

const MODE_OF_INTERVIEW = [
  "ScreenIt",
  "Interview",
  "Zoom",
  "WebEx",
  "In Person",
  "Hangout Teams",
  "Skype",
  "Microsoft Teams",
];
const LEVEL_OF_INTERVIEW = ["Junior", "Mid", "Senior", "Lead", "Architect"];

const AddFeedbackNamIT = ({ isEdit }: { isEdit: boolean }) => {
  const { schedule_id } = useParams();
  const [feedbackData, setFeedbackData] = useState<InterviewFeedbackProps>(
    INITIAl_FEEDBACK_DATA
  );

  const [errors, setErrors] = useState<
    Partial<Record<keyof InterviewFeedbackProps, string>>
  >({});

  const [primarySkills, setPrimarySkills] = useState<SkillInputProps[]>([]);
  const [secondarySkills, setSecondarySkills] = useState<SkillInputProps[]>([]);
  const [mandatorySkills, setMandatorySkills] = useState<SkillInputProps[]>([]);

  const [scheduleDetail, setScheduleDetail] =
    useState<InterviewScheduleDetailProps | null>(null);
  const [jdDetail, setJdDetail] = useState<any>(null);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (schedule_id) {
      fetchScheduleDetails(schedule_id);
    }
  }, [schedule_id]);

  const fetchScheduleDetails = async (scheduleId: string) => {
    setLoading(true);
    try {
      const { data } = await orgApi.getScheduleData(scheduleId);
      if (data.success) {
        setScheduleDetail(data.schedule);

        const fb = data?.schedule?.feedback?.[0];
        const completed = timeValidity(fb?.created_on);
        if (completed || isEdit) {
          setIsCompleted(completed);
          setFeedbackData(fb);
          fetchPostDetails(data?.schedule?.post_id, fb, completed);
          fetchMandatorySkills(fb, completed);
        } else {
          setFeedbackData({
            ...INITIAl_FEEDBACK_DATA,
            url: data?.schedule?.url || null,
          });
          fetchPostDetails(data?.schedule?.post_id);
          fetchMandatorySkills();
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while fetching schedule details");
    } finally {
      setLoading(false);
    }
  };

  const timeValidity = (fixedDateTimeString: string) => {
    const currentDateTime = new Date();
    const fixedDateTime = new Date(fixedDateTimeString);
    const fixedDateTimePlusOneDay = new Date(fixedDateTime);
    fixedDateTimePlusOneDay.setDate(fixedDateTime.getDate() + 1);
    return currentDateTime < fixedDateTimePlusOneDay;
  };

  interface TemplateSkill {
    skill: {
      id: string;
      name: string;
    };
    weightage: number;
  }

  interface FeedbackSkill {
    experience?: string;
    remarks?: string;
    skill_rating: number;
    skill: {
      id: string;
      name: string;
    };
  }

  interface FeedbackData {
    fp_skill?: FeedbackSkill[];
    fs_skill?: FeedbackSkill[];
    fm_skill?: FeedbackSkill[];
  }

  const mapToSkillInput = (skillArray: TemplateSkill[]): SkillInputProps[] =>
    skillArray.map((el) => ({
      skill_name: el.skill.name,
      skill_id: el.skill.id,
      weightage: el.weightage,
      skill_rating: 0,
      experience: "",
    }));

  const mapFeedbackToSkillInput = (
    feedbackSkills: FeedbackSkill[]
  ): SkillInputProps[] =>
    feedbackSkills.map((skill) => ({
      skill_name: skill.skill.name,
      skill_id: skill.skill.id,
      weightage: 0,
      skill_rating: skill.skill_rating || 0,
      experience: skill.experience || "",
    }));

  const mapTemplateToSkillInput = (
    templateSkills: TemplateSkill[]
  ): SkillInputProps[] =>
    templateSkills?.map((skill) => ({
      skill_name: skill.skill.name,
      skill_id: skill.skill.id,
      weightage: skill.weightage,
      skill_rating: 0,
      experience: "",
    })) || [];

  const getSkillsFeedbackOrTemplate = (
    feedbackSkills?: FeedbackSkill[],
    templateSkills?: TemplateSkill[]
  ): SkillInputProps[] => {
    if (feedbackSkills && feedbackSkills?.length > 0) {
      return mapFeedbackToSkillInput(feedbackSkills);
    }
    return mapTemplateToSkillInput(templateSkills || []);
  };

  const fetchPostDetails = async (
    postId: string,
    feedback?: FeedbackData,
    completed?: boolean
  ) => {
    try {
      const { data } = await intApi.getSingleJdDetail(postId);

      if (!data.success) {
        toast.error(data.msg || "Failed to fetch post details.");
        return;
      }

      setJdDetail(data.post);

      const template = data?.post?.template;

      if (completed || isEdit) {
        const p_Skills = getSkillsFeedbackOrTemplate(
          feedback?.fp_skill,
          template?.t_p_skill
        );

        const s_Skills = getSkillsFeedbackOrTemplate(
          feedback?.fs_skill,
          template?.t_s_skill
        );

        setPrimarySkills(p_Skills);
        setSecondarySkills(s_Skills);
      } else {
        const p_Skills = mapToSkillInput(template?.t_p_skill || []);
        const s_Skills = mapToSkillInput(template?.t_s_skill || []);

        setPrimarySkills(p_Skills);
        setSecondarySkills(s_Skills);
      }
    } catch (error) {
      console.error("Error fetching post details:", error);
      toast.error("An error occurred while fetching post details.");
    }
  };

  interface MandatorySkill {
    skill: { id: string; name: string };
  }

  const mapMandatorySkills = (
    manSkills: MandatorySkill[]
  ): SkillInputProps[] => {
    return manSkills && manSkills.length > 0
      ? manSkills.map((e) => ({
          skill_name: e.skill.name,
          skill_id: e.skill.id,
          skill_rating: 0,
          experience: "",
        }))
      : [];
  };

  const prefillMandatorySkills = (
    manSkills: {
      skill: { id: string; name: string };
      skill_rating: number;
      remarks?: string;
    }[]
  ) => {
    return manSkills && manSkills.length > 0
      ? manSkills.map((skill) => ({
          skill_name: skill?.skill.name,
          skill_id: skill?.skill.id,
          skill_rating: skill?.skill_rating,
          experience: skill?.remarks || "",
        }))
      : [];
  };

  const fetchMandatorySkills = async (
    feedback?: FeedbackData,
    completed?: boolean
  ) => {
    try {
      const { data } = await orgApi.getMandatorySkills();
      if (data.success) {
        if (completed || isEdit) {
          const mskill =
            feedback?.fm_skill && feedback.fm_skill.length > 0
              ? prefillMandatorySkills(feedback.fm_skill)
              : mapMandatorySkills(data.man_skill);

          setMandatorySkills(mskill);
        } else {
          const mskill = mapMandatorySkills(data.man_skill);
          setMandatorySkills(mskill);
        }
      } else {
        toast.error(data.msg || "Failed to fetch mandatory skills.");
      }
    } catch (error) {
      console.error("Error fetching mandatory skills:", error);
      toast("An error occurred while fetching mandatory skills.");
    }
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setErrors((prev) => ({ ...prev, image: "" }));
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors({
        ...errors,
        url: "Please select a valid image file",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors({
        ...errors,
        url: "File size exceeds 2MB. Please upload a smaller file.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result;
      if (typeof result !== "string") {
        setErrors((prev) => ({ ...prev, image: "Could not read the file." }));
      } else {
        const uploadedUrl = await fileCheck(result);
        if (uploadedUrl) {
          setFeedbackData((prev) => ({ ...prev, url: uploadedUrl }));
          setErrors((prev) => ({ ...prev, image: "" }));
        }
      }
    };

    reader.readAsDataURL(file);
  };

  const validateSkill = (skill: SkillInputProps): string => {
    const errors: string[] = [];

    if (skill.skill_rating === 0) {
      errors.push("Rating is required");
    }

    if (!skill.experience.trim()) {
      errors.push("Experience is required");
    }

    return errors.join(", ");
  };

  const handleSkillChange = (
    category: string,
    skillId: string,
    field: string,
    value: any
  ) => {
    const updateFunction = {
      primary: setPrimarySkills,
      secondary: setSecondarySkills,
      mandatory: setMandatorySkills,
    }[category];

    if (updateFunction) {
      updateFunction((prev) =>
        prev.map((skill) =>
          skill.skill_id === skillId
            ? { ...skill, [field]: value, error: "" }
            : skill
        )
      );
    }
  };

  const handleRatingChange = (
    category: string,
    skillId: string,
    rating: number
  ) => {
    const updateFunction = {
      primary: setPrimarySkills,
      secondary: setSecondarySkills,
      mandatory: setMandatorySkills,
    }[category];

    if (updateFunction) {
      updateFunction((prev) =>
        prev.map((skill) =>
          skill.skill_id === skillId
            ? { ...skill, skill_rating: rating, error: "" }
            : skill
        )
      );
    }
  };

  const validateAllSkills = (): boolean => {
    let isValid = true;

    const validateCategory = (
      skills: SkillInputProps[],
      setSkills: React.Dispatch<React.SetStateAction<SkillInputProps[]>>
    ) => {
      const updatedSkills = skills.map((skill) => {
        const error = validateSkill(skill);
        if (error) isValid = false;
        return { ...skill, error };
      });
      setSkills(updatedSkills);
    };

    validateCategory(primarySkills, setPrimarySkills);
    validateCategory(secondarySkills, setSecondarySkills);
    validateCategory(mandatorySkills, setMandatorySkills);

    return isValid;
  };

  const validateFeedbackForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!feedbackData.mode) {
      newErrors.mode = "Mode is required";
    }

    if (!feedbackData.level) {
      newErrors.level = "Level is required";
    }

    if (
      !feedbackData.junior &&
      !feedbackData.mid &&
      !feedbackData.senior &&
      !feedbackData.lead &&
      !feedbackData.architect
    ) {
      newErrors.junior = `${feedbackData.level} is required`;
    }

    if (!feedbackData.communication_skill) {
      newErrors.communication_skill = "Communication skill is required";
    }

    if (!feedbackData.problem_solving_cmd) {
      newErrors.problem_solving_cmd = "Problem solving skill is required";
    }

    if (!feedbackData.observation) {
      newErrors.observation = "Observation is required";
    }

    if (!feedbackData.attitude_confidence_cmd) {
      newErrors.attitude_confidence_cmd = "Attitude and confidence is required";
    }

    if (!feedbackData.inter_personal_skill_cmd) {
      newErrors.inter_personal_skill_cmd = "Inter personal skill is required";
    }

    if (!feedbackData.recommended_status) {
      newErrors.recommended_status = "Recommended status is required";
    }
    if (!feedbackData.professionalism_cmd) {
      newErrors.professionalism_cmd = "Professionalism is required";
    }

    if (!feedbackData.comments) {
      newErrors.comments = "Comments is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const skillMapper = (skills: SkillInputProps[]) => {
    return skills.map((skill) => ({
      skill_name: skill.skill_name,
      skill_id: skill.skill_id,
      weightage: skill.weightage,
      skill_rating: skill.skill_rating,
      experience: skill.experience,
    }));
  };
  const mandatorySkillMapper = (skills: SkillInputProps[]) => {
    return skills.map((skill) => ({
      skill_name: skill.skill_name,
      skill_id: skill.skill_id,
      skill_rating: skill.skill_rating,
      remarks: skill.experience,
    }));
  };

  const handleSubmit = async () => {
    if (feedbackData.candidate_no_show || feedbackData.proxy) {
      const bodyData = {
        schedule_id: schedule_id,
        interview_status: "Incomplete",
        candidate_no_show: feedbackData.candidate_no_show,
        proxy: feedbackData.proxy,
        url: feedbackData.url || "",
        primary_skill: [],
        secondary_skill: [],
        mandatory_skill: [],
      };

      if ((isCompleted || isEdit) && scheduleDetail?.feedback?.[0]?.id) {
        handleEditFeedback(scheduleDetail?.feedback?.[0]?.id, bodyData);
      } else {
        handleAddFeedback(schedule_id!, bodyData);
      }
    } else {
      if (validateAllSkills() && validateFeedbackForm()) {
        const bodyData = {
          ...feedbackData,
          interview_status: "Completed",
          schedule_id,
          primary_skill: skillMapper(primarySkills),
          secondary_skill: skillMapper(secondarySkills),
          mandatory_skill: mandatorySkillMapper(mandatorySkills),
          url: feedbackData.url || "",
        };

        if ((isCompleted || isEdit) && scheduleDetail?.feedback?.[0]?.id) {
          handleEditFeedback(scheduleDetail?.feedback?.[0]?.id, bodyData);
        } else {
          handleAddFeedback(schedule_id!, bodyData);
        }
      } else {
        toast.error("Please fill all the required fields");
      }
    }
  };

  const handleAddFeedback = async (scheduleId: string, bodyData: any) => {
    setLoading(true);
    try {
      const { data } = await intApi.addFeedback(bodyData, scheduleId);
      if (data.success) {
        toast.success(data.msg);
        handleNavigate();
      } else {
        toast.warn(data.msg);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditFeedback = async (feedbackId: string, req: any) => {
    setLoading(true);
    try {
      const { data } = await intApi.editFeedback(req, feedbackId);
      if (data.success) {
        toast.success(data.msg);
        handleNavigate();
      } else {
        toast.warn(data.msg);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    setTimeout(() => {
      window.location.replace("https://screenit.io");
    }, 500);
  };

  const candidate = scheduleDetail?.candidate;
  const interviewer = scheduleDetail?.interviewer;

  const handleFeedbackFieldChange = (
    field: string,
    value: string | boolean
  ) => {
    setFeedbackData({ ...feedbackData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const isProxy = feedbackData.candidate_no_show || feedbackData.proxy;

  const fileCheck = async (file: string): Promise<string> => {
    try {
      const base64Url = await urlToBase64(file);
      if (!base64Url) {
        toast("Failed to convert image to base64.");
        return "";
      }

      const newFile = await dataURLtoFile(
        base64Url as string,
        Math.random().toString(36).substring(6) + "_name.png"
      );

      const formData = new FormData();
      formData.append("pic", newFile);

      const res1 = await orgApi.getUrl(formData);
      if (res1.data.success) {
        return res1.data.url;
      } else {
        toast("Please attach the candidate profile");
        return "";
      }
    } catch (error) {
      console.error("Error in fileCheck:", error);
      toast("Something went wrong while uploading the image.");
      return "";
    }
  };

  const dataURLtoFile = async (dataurl: any, filename: any) => {
    var arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const urlToBase64 = async (url: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      return new Promise((resolve, reject) => {
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
      });
    } catch (error) {
      console.error("Error converting URL to base64:", error);
      return null;
    }
  };

  return (
    <>
      <div className="flex justify-center bg-gray-100 w-full min-h-screen">
        {scheduleDetail?.feedback_status === "Completed" &&
          !isCompleted &&
          !isEdit && <FeedbackSubmitted />}
        {(scheduleDetail?.feedback_status !== "Completed" ||
          isCompleted ||
          isEdit) && (
          <div className="max-w-2xl bg-white pl-5 pr-5">
            <div className="flex justify-center items-center border-b">
              <div className="py-6">
                <h1 className="text-sm font-semibold">
                  Interactive Technical Assessment
                </h1>
              </div>
            </div>
            <div className="grid grid-cols-3 bg-primary border-primary p-4 gap-3 rounded-tl-lg rounded-br-lg">
              <div className="">
                <h1 className="text-gray-200 text-xs font-light ">
                  Candidate Name
                </h1>
                <h1 className="text-white text-xs font-bold">
                  {candidate?.first_name} {candidate?.last_name || ""}
                </h1>
              </div>
              <div className="">
                <h1 className="text-gray-200 text-xs font-light">
                  Panelist's Name
                </h1>
                <h1 className="text-white text-xs font-bold">
                  {interviewer?.candidate?.first_name}{" "}
                  {interviewer?.candidate?.last_name || ""}
                </h1>
              </div>
              <div className="">
                <h1 className="text-gray-200 text-xs font-light">
                  Recruiter's Name:
                </h1>
                <h1 className="text-white text-xs font-bold">
                  {scheduleDetail?.org.first_name}{" "}
                  {scheduleDetail?.org?.last_name || ""}
                </h1>
              </div>
              <div className="">
                <h1 className="text-gray-200 text-xs font-light">Role</h1>
                <h1 className="text-white text-xs font-bold">
                  {jdDetail?.job_title}
                </h1>
              </div>
              <div className="">
                <h1 className="text-gray-200 text-xs font-light">
                  Date(DD MMM YYYY):
                </h1>
                <h1 className="text-white text-xs font-bold">
                  {scheduleDetail?.utc_starttime &&
                    Timer.getDate(new Date(scheduleDetail?.utc_starttime))}
                </h1>
              </div>
              <div className="">
                <h1 className="text-gray-200 text-xs font-light">
                  Name of the Client:
                </h1>
                <h1 className="text-white text-xs font-bold">
                  {jdDetail?.client_name || "NA"}
                </h1>
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-3 items-center my-3 justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-xs font-normal text-gray-500">
                    Candidate No Show
                  </p>
                  <div className="flex gap-4">
                    {[true, false].map((option, index) => (
                      <RadioOption
                        key={index}
                        label={option ? "Yes" : "No"}
                        id={`candidate_no_show_${index}`}
                        name="candidate_no_show"
                        value={option.toString()}
                        checked={feedbackData?.candidate_no_show === option}
                        onChange={() =>
                          setFeedbackData({
                            ...feedbackData,
                            candidate_no_show: option,
                            proxy: false,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <p className="text-xs font-normal text-gray-500">
                    Candidate Proxy
                  </p>
                  <div className="flex gap-4">
                    {[true, false].map((option, index) => (
                      <RadioOption
                        key={index}
                        id={`candidate_proxy_${index}`}
                        label={option ? "Yes" : "No"}
                        name="candidate_proxy"
                        value={option.toString()}
                        checked={feedbackData?.proxy === option}
                        onChange={() =>
                          setFeedbackData({
                            ...feedbackData,
                            proxy: option,
                            candidate_no_show: false,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="justify-between items-center my-3 grid grid-cols-2 gap-3">
                <Dropdown
                  label="Mode of Interview"
                  required
                  options={MODE_OF_INTERVIEW.map((el) => ({
                    value: el,
                    label: el,
                  }))}
                  value={feedbackData.mode}
                  onChange={(obj) =>
                    handleFeedbackFieldChange("mode", obj.value)
                  }
                  error={errors.mode}
                />
                <div className={isProxy ? "disabled-div" : ""}>
                  <Dropdown
                    required
                    label="Level of Interview"
                    options={LEVEL_OF_INTERVIEW.map((el) => ({
                      value: el,
                      label: el,
                    }))}
                    value={feedbackData.level}
                    onChange={(obj) => {
                      setFeedbackData({
                        ...feedbackData,
                        level: obj.value,
                        junior: "",
                        mid: "",
                        senior: "",
                        lead: "",
                        architect: "",
                      });
                      setErrors({
                        ...errors,
                        junior: "",
                        mid: "",
                        senior: "",
                        lead: "",
                        architect: "",
                      });
                    }}
                    error={errors.level}
                    disabled={isProxy}
                  />
                </div>
              </div>

              <ScoringDefinition tag={isProxy} />

              <div
                className={
                  isProxy
                    ? "disabled-div flex flex-col items-start gap-2 my-3 w-full"
                    : "flex flex-col items-start gap-2 my-3 w-full"
                }
              >
                <div className="pt-2">
                  <h1 className="text-sm font-semibold text-gray-800 mb-2">
                    Technical Evaluation
                  </h1>
                  <h1 className="text-xs font-normal text-gray-400">
                    Please evaluate the candidate on the following on a scale of
                    1 to 5 (1 Lowest, 5 Highest)
                  </h1>
                </div>
                {primarySkills && primarySkills.length > 0 && (
                  <SkillCategory
                    title="Primary Skills"
                    skills={primarySkills}
                    onSkillChange={(skillId, field, value) =>
                      handleSkillChange("primary", skillId, field, value)
                    }
                    onRatingChange={(skillId, rating) =>
                      handleRatingChange("primary", skillId, rating)
                    }
                  />
                )}

                {secondarySkills && secondarySkills.length > 0 && (
                  <SkillCategory
                    title="Secondary Skills"
                    skills={secondarySkills}
                    onSkillChange={(skillId, field, value) =>
                      handleSkillChange("secondary", skillId, field, value)
                    }
                    onRatingChange={(skillId, rating) =>
                      handleRatingChange("secondary", skillId, rating)
                    }
                  />
                )}

                {mandatorySkills && mandatorySkills.length > 0 && (
                  <SkillCategory
                    title="Mandatory Skills"
                    skills={mandatorySkills}
                    onSkillChange={(skillId, field, value) =>
                      handleSkillChange("mandatory", skillId, field, value)
                    }
                    onRatingChange={(skillId, rating) =>
                      handleRatingChange("mandatory", skillId, rating)
                    }
                  />
                )}
              </div>

              <ExperienceFeedback
                isProxy={isProxy}
                errors={errors}
                feedbackData={feedbackData}
                handleFeedbackFieldChange={handleFeedbackFieldChange}
              />

              <CommunicationSection
                isProxy={isProxy}
                feedbackData={feedbackData}
                errors={errors}
                handleFeedbackFieldChange={handleFeedbackFieldChange}
              />

              <ObservationFeedback
                isProxy={isProxy}
                errors={errors}
                feedbackData={feedbackData}
                handleFeedbackFieldChange={handleFeedbackFieldChange}
              />
              <FinalRecommendation
                isProxy={isProxy}
                errors={errors}
                feedbackData={feedbackData}
                handleFeedbackFieldChange={handleFeedbackFieldChange}
              />
              <div
                className={`flex flex-col items-start gap-2 my-3 w-full ${
                  isProxy ? "disabled-div" : ""
                }`}
              >
                <div className="pt-2">
                  <h1 className="text-sm font-semibold text-gray-800 mb-2">
                    Recommended? <RequiredField />
                  </h1>

                  <div className="flex gap-4">
                    {[true, false].map((option, index) => (
                      <RadioOption
                        key={index}
                        id={`eligible_for_this_position_${index}`}
                        label={option ? "Yes" : "No"}
                        name="eligible_for_this_position"
                        value={option.toString()}
                        checked={
                          feedbackData?.eligible_for_this_position === option
                        }
                        onChange={() =>
                          handleFeedbackFieldChange(
                            "eligible_for_this_position",
                            option
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>

              <ProfileImageUpload
                label={
                  feedbackData.candidate_no_show
                    ? "Candidate Did Not Attend"
                    : feedbackData.proxy
                    ? "Possible Proxy Candidate"
                    : "Candidate Profile Picture"
                }
                selectedImage={feedbackData.url}
                error={errors.url}
                onImageChange={handleImageChange}
              />
              <div className="py-10 flex justify-end">
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className={
                    "flex items-center px-3 py-2 text-white text-sm font-semibold button-primary border rounded-md mb-3"
                  }
                >
                  Submit
                </button>
              </div>
            </div>
            {scheduleDetail?.video_url && (
              <div className="my-2 flex flex-col gap-2">
                {(() => {
                  const videoUrl = scheduleDetail.video_url;
                  const extension = GS.getFileExtension(videoUrl);

                  if (!videoUrl) return null;

                  const commonProps = {
                    controls: true,
                    autoPlay: false,
                    width: "750",
                    height: "500",
                  };

                  return extension !== "mp4" ? (
                    <ReactHlsPlayer {...commonProps} src={videoUrl} />
                  ) : (
                    <video
                      {...commonProps}
                      src={videoUrl}
                      title="Interview Recording"
                    />
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
      {loading && (
        <div
          className={
            "absolute top-0 left-0 right-0 bottom-0 z-50 bg-white flex justify-center items-center"
          }
        >
          <BiLoader size={20} className="animate-spin text-indigo-600" />
        </div>
      )}
    </>
  );
};

export default AddFeedbackNamIT;

const RadioOption = ({
  label,
  id,
  name,
  value,
  checked,
  onChange,
}: {
  label: string;
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <label className="flex items-center gap-1 cursor-pointer">
    <input
      type="radio"
      id={id}
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="cursor-pointer"
      style={{ accentColor: "#2563eb" }}
    />
    <span className="text-black text-xs font-bold">{label}</span>
  </label>
);

interface SkillCategoryProps {
  title: string;
  skills: SkillInputProps[];
  onSkillChange: (skillId: string, field: string, value: string) => void;
  onRatingChange: (skillId: string, rating: number) => void;
}

const SkillCategory = ({
  title,
  skills,
  onSkillChange,
  onRatingChange,
}: SkillCategoryProps) => {
  return (
    <div className=" w-full">
      <h3 className="text-sm 2xl:text-base font-semibold mb-2 text-gray-800">
        {title}
      </h3>

      <div className="space-y-4">
        {skills.map((skill) => (
          <div
            key={skill.skill_id}
            className="p-4 bg-gray-50 rounded-lg text-xs 2xl:text-sm"
          >
            <div className="grid grid-cols-3 gap-4 items-center ">
              <div className="">
                <h4 className="font-medium text-gray-700 mb-2">
                  {skill.skill_name} <RequiredField />
                </h4>
                {skill.weightage && (
                  <span className=" bg-blue-50 px-2 py-1 rounded-full text-blue-500 text-[10px] 2xl:text-xs">
                    Weightage: {skill.weightage}%
                  </span>
                )}
              </div>

              <div className="text-sm 2xl:text-base">
                <div className="flex items-center">
                  <StarRating
                    stars={skill.skill_rating}
                    callBack={(rating: number) =>
                      onRatingChange(skill.skill_id, rating)
                    }
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    ({skill.skill_rating})
                  </span>
                </div>
              </div>

              <TextArea
                name={`comment-${skill.skill_id}`}
                value={skill.experience}
                onChange={(e) =>
                  onSkillChange(skill.skill_id, "experience", e.target.value)
                }
                placeholder="Enter your comments..."
              />
            </div>
            {skill.error && (
              <p className="text-[10px] 2xl:text-xs text-red-500 mt-2">
                {skill.error}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EXPERIENCE_LEVELS: { label: string; key: string }[] = [
  { label: "Junior", key: "junior" },
  { label: "Mid", key: "mid" },
  { label: "Senior", key: "senior" },
  { label: "Lead", key: "lead" },
  { label: "Architect", key: "architect" },
];

interface ExperienceFeedbackProps {
  isProxy: boolean;
  feedbackData: InterviewFeedbackProps;
  errors?: Partial<Record<string, string>>;
  handleFeedbackFieldChange: (
    key: keyof InterviewFeedbackProps,
    value: string
  ) => void;
}

const ExperienceFeedback = ({
  isProxy,
  feedbackData,
  errors,
  handleFeedbackFieldChange,
}: ExperienceFeedbackProps) => {
  return (
    <div
      className={`flex flex-col items-start gap-2 my-3 w-full ${
        isProxy ? "disabled-div" : ""
      }`}
    >
      <div className="pt-2 w-full">
        <h1 className="text-sm font-semibold text-gray-800 mb-2">
          Level of Experience
        </h1>

        <table className="table-fixed w-full mt-2">
          <thead className="text-sm text-left">
            <tr>
              <th className="w-1/2 p-2 text-xs">Category</th>
              <th className="w-1/2 p-2 text-xs">Comments</th>
            </tr>
          </thead>
          <tbody className="">
            {EXPERIENCE_LEVELS.map(({ label, key }) => (
              <tr key={key} className="bg-gray-50 border-b-2 border-white">
                <td className="p-2 text-xs text-gray-500">
                  {label} {feedbackData.level === label && <RequiredField />}
                </td>
                <td className="p-2">
                  <TextArea
                    name={`${key}_level`}
                    aria-label={`${label} level feedback`}
                    value={
                      (feedbackData[
                        key as keyof InterviewFeedbackProps
                      ] as string) || ""
                    }
                    onChange={(e) =>
                      handleFeedbackFieldChange(
                        key as keyof InterviewFeedbackProps,
                        e.target.value
                      )
                    }
                    disabled={isProxy || feedbackData.level !== label}
                  />
                  {errors?.junior && feedbackData.level === label && (
                    <p className="text-red-500 text-xs mt-1">{errors.junior}</p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface ObservationField {
  name: keyof InterviewFeedbackProps;
  label: string;
}

const OBSERVATION_FIELDS: ObservationField[] = [
  {
    name: "observation",
    label: "Technical Observation",
  },
  {
    name: "problem_solving_cmd",
    label: "Analytical and problem-solving Skills",
  },
  {
    name: "attitude_confidence_cmd",
    label: "Attitude / Aptitude demonstrated",
  },
  {
    name: "inter_personal_skill_cmd",
    label: "Clarity of the answers provided",
  },
];

interface ObservationProps {
  isProxy: boolean;
  feedbackData: InterviewFeedbackProps;
  errors?: Partial<Record<keyof InterviewFeedbackProps, string>>;
  handleFeedbackFieldChange: (
    key: keyof InterviewFeedbackProps,
    value: string
  ) => void;
}

const ObservationFeedback = ({
  isProxy,
  feedbackData,
  errors,
  handleFeedbackFieldChange,
}: ObservationProps) => {
  return (
    <div
      className={`flex flex-col items-start gap-2 my-3 w-full ${
        isProxy ? "disabled-div" : ""
      }`}
    >
      <div className="pt-2 w-full">
        <h1 className="text-sm font-semibold text-gray-800 mb-2">
          Observations
        </h1>

        <table className="table-fixed w-full mt-2">
          <thead className="text-sm">
            <tr>
              <th className="w-1/2 p-2 text-xs text-left">
                Communication Level
              </th>
              <th className="w-1/2 p-2 text-xs text-left">
                (Ref one of the four listed above)
              </th>
            </tr>
          </thead>
          <tbody className="">
            {OBSERVATION_FIELDS.map((field) => (
              <tr
                key={field.name}
                className="bg-gray-50 border-b-2 border-white"
              >
                <td className="p-2 text-xs text-gray-500">
                  {field.label} <RequiredField />
                </td>
                <td className="p-2">
                  <TextArea
                    name={field.name}
                    value={
                      (feedbackData[
                        field.name as keyof InterviewFeedbackProps
                      ] as string) || ""
                    }
                    onChange={(e) =>
                      handleFeedbackFieldChange(field.name, e.target.value)
                    }
                    placeholder="Enter Comments"
                    disabled={isProxy}
                  />
                  {errors?.[field.name] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors[field.name]}
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface CommunicationSectionProps {
  isProxy: boolean;
  feedbackData: InterviewFeedbackProps;
  handleFeedbackFieldChange: (
    key: keyof InterviewFeedbackProps,
    value: string
  ) => void;
  errors?: Partial<Record<keyof InterviewFeedbackProps, string>>;
}

const CommunicationSection = ({
  isProxy,
  feedbackData,
  errors,
  handleFeedbackFieldChange,
}: CommunicationSectionProps) => {
  return (
    <div
      className={`flex flex-col items-start gap-2 my-3 w-full ${
        isProxy ? "disabled-div" : ""
      }`}
    >
      <p className="font-semibold text-xs underline text-gray-900">
        Communication level of expertise (Defined standard, level of oral
        articulation) <RequiredField />
      </p>

      <div className="flex flex-col items-start justify-start ml-6 gap-2">
        {COMMUNICATION_OPTIONS.map((option) => (
          <RadioOption
            key={option.value}
            id={`communication_level_${option.value}`}
            name="communication_skill"
            value={option.value}
            label={option.label}
            checked={feedbackData.communication_skill === option.value}
            onChange={(e) =>
              handleFeedbackFieldChange("communication_skill", e.target.value)
            }
          />
        ))}
      </div>
      {errors?.communication_skill && (
        <p className="text-red-500 text-xs mt-1">
          {errors.communication_skill}
        </p>
      )}
    </div>
  );
};

const COMMUNICATION_OPTIONS = [
  {
    value: "1",
    label:
      "1. Speaks with limited fluency, irregular grammar and carries a distinct accent",
  },
  {
    value: "2",
    label:
      "2. Speaks with comfortable fluency, agreeable grammar, has noticeable accent",
  },
  {
    value: "3",
    label:
      "3. Speaks with acceptable finesse/accurate grammar and has no accent",
  },
  {
    value: "4",
    label:
      "4. Speaks with polish/finesse. Demonstrates confidence in presenting the credentials with ease",
  },
];

interface FinalRecommendationProps {
  isProxy: boolean;
  feedbackData: InterviewFeedbackProps;
  handleFeedbackFieldChange: (
    key: keyof InterviewFeedbackProps,
    value: string
  ) => void;
  errors?: Partial<Record<keyof InterviewFeedbackProps, string>>;
}

const FinalRecommendation = ({
  isProxy,
  feedbackData,
  handleFeedbackFieldChange,
  errors,
}: FinalRecommendationProps) => {
  return (
    <div
      className={`flex flex-col items-start gap-2 my-3 w-full ${
        isProxy ? "disabled-div" : ""
      }`}
    >
      <div className="pt-2 w-full">
        <h1 className="text-sm font-semibold text-gray-800 mb-2">
          Final Recommendation
        </h1>

        <table className="table-fixed w-full mt-2">
          <tbody className="">
            {FINAL_RECOMMENDATION_FIELDS.map((field) => (
              <tr
                key={field.name}
                className="bg-gray-50 border-b-2 border-white"
              >
                <td className="p-2 text-xs text-gray-500 w-1/2">
                  {field.label}
                  <RequiredField />
                </td>
                <td className="p-2 w-1/2">
                  <TextArea
                    name={field.name}
                    value={
                      (feedbackData[
                        field.name as keyof InterviewFeedbackProps
                      ] as string) || ""
                    }
                    onChange={(e) =>
                      handleFeedbackFieldChange(
                        field.name as keyof InterviewFeedbackProps,
                        e.target.value
                      )
                    }
                    placeholder="Enter Comments"
                    disabled={isProxy}
                  />
                  {errors?.[field.name as keyof InterviewFeedbackProps] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors?.[field.name as keyof InterviewFeedbackProps]}
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RequiredField = () => {
  return <span className="text-red-500">*</span>;
};

interface StarRatingProps {
  stars: number;
  callBack?: (rating: number) => void;
}

const StarRating = ({ stars, callBack }: StarRatingProps) => {
  const getStarIcon = (index: number) => {
    if (stars >= index + 1) {
      return <FaStar className="text-yellow-400" />;
    } else if (stars >= index + 0.5) {
      return <FaStarHalfAlt className="text-yellow-400" />;
    } else {
      return <FaRegStar className="text-gray-400" />;
    }
  };

  const handleClick = (rating: number) => {
    if (callBack) callBack(rating);
  };

  return (
    <div className="flex space-x-1 cursor-pointer">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="relative group">
          <div
            onClick={() => handleClick(i + 1)}
            className="absolute left-1/2 w-1/2 h-full z-10"
          />
          <div
            onClick={() => handleClick(i + 0.5)}
            className="absolute left-0 w-1/2 h-full z-10"
          />
          <div className="text-xl">{getStarIcon(i)}</div>
        </div>
      ))}
    </div>
  );
};
